import {
  formatEatDate,
  formatMoney,
  moneyFromCents,
  type ItemCodeSystem,
  type ServiceItemCategory,
  type ServiceItemKind,
  type SplitMethod,
} from '@wodi/domain';
import type { AmountView, PayerKind, SplitTermsView } from '@wodi/shared';

// Words for the finance settings screens (DESIGN.md §9: sentence case, plain words).

export const CATEGORY_LABELS: Record<ServiceItemCategory, string> = {
  consultation: 'Consultation',
  procedure: 'Procedure',
  lab_test: 'Lab test',
  imaging: 'Imaging',
  medicine: 'Medicine',
  consumable: 'Consumable',
  bed_day: 'Bed-day',
  nursing: 'Nursing',
  mortuary: 'Mortuary',
  other: 'Other',
};

export const KIND_LABELS: Record<ServiceItemKind, string> = {
  service: 'Service',
  stock_item: 'Stock item',
};

export const CODE_SYSTEM_LABELS: Record<ItemCodeSystem, string> = {
  sha_intervention: 'SHA intervention',
  knhts: 'KNHTS',
  loinc: 'LOINC',
  atc: 'ATC',
  legacy: 'Own code',
};

export const PAYER_KIND_LABELS: Record<PayerKind, string> = {
  cash: 'Cash',
  sha: 'SHA',
  insurer: 'Insurer',
  corporate: 'Corporate',
  other: 'Other',
};

export const METHOD_LABELS: Record<SplitMethod, string> = {
  payer_pays_all: 'Payer pays all',
  patient_pays_all: 'Patient pays all',
  patient_copay_percent: 'Patient pays a percentage',
  patient_copay_fixed_per_unit: 'Patient pays a fixed amount per unit',
  payer_limit_per_unit: 'Payer pays up to a limit per unit',
};

/** `KES 1,200` from integer cents on the wire. */
export function money(amount: AmountView | null): string {
  return amount === null ? 'No price' : formatMoney(moneyFromCents(BigInt(amount.cents)));
}

export function day(iso: string): string {
  return formatEatDate(new Date(iso));
}

/** Basis points as a percentage in words: 1250 is "12.5%". */
export function percent(basisPoints: number): string {
  const whole = Math.trunc(basisPoints / 100);
  const fraction = basisPoints % 100;
  return fraction === 0
    ? `${String(whole)}%`
    : `${String(whole)}.${String(fraction).padStart(2, '0').replace(/0$/, '')}%`;
}

/** A rule as a sentence: "Patient pays 20%, the payer pays the rest". */
export function describeTerms(terms: SplitTermsView, payerName: string): string {
  switch (terms.method) {
    case 'payer_pays_all':
      return `${payerName} pays all of it`;
    case 'patient_pays_all':
      return 'The patient pays all of it';
    case 'patient_copay_percent':
      return `The patient pays ${percent(terms.copayBasisPoints)}, ${payerName} pays the rest`;
    case 'patient_copay_fixed_per_unit':
      return `The patient pays ${money(terms.amountPerUnit)} per unit, ${payerName} pays the rest`;
    case 'payer_limit_per_unit':
      return `${payerName} pays up to ${money(terms.amountPerUnit)} per unit, the patient pays the rest`;
  }
}
